import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../integrations/supabase/client';
import { getUserCompleteProfile } from '../utils/userProfileCoordinator';
import { ensureUserConsent } from '@/services/LegalService';

// 🚀 PROFILE CACHE: Unified caching with TTL and invalidation
const CACHE_KEY = 'blissnet_profile_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

const getCachedProfile = (userId: string) => {
  try {
    const cacheKey = `${CACHE_KEY}_${userId}`;
    const cached = localStorage.getItem(cacheKey);

    if (!cached) {
      return null;
    }

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    const isExpired = age > CACHE_TTL;

    if (isExpired) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('GlobalProfileContext getCachedProfile: Error reading cache:', error);
    return null;
  }
};

const setCachedProfile = (userId: string, data: any) => {
  try {
    const cacheKey = `${CACHE_KEY}_${userId}`;
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('GlobalProfileContext setCachedProfile: Error caching data:', error);
  }
};

const invalidateProfileCache = (userId: string) => {
  try {
    const cacheKey = `${CACHE_KEY}_${userId}`;
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('GlobalProfileContext invalidateProfileCache: Error clearing cache:', error);
  }
};

const calcAge = (dob: string) => {
  const t = new Date();
  const b = new Date(dob);
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
};

interface GlobalProfileContextType {
  profile: any;
  currentUser: any;
  isLoading: boolean;
  isInitialized: boolean;
  updateProfile: (updates: any) => Promise<void>;
  refreshProfile: () => Promise<void>;
  initializeProfile: () => Promise<void>;
}

const GlobalProfileContext = createContext<GlobalProfileContextType | undefined>(undefined);

export function GlobalProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize profile data once when provider mounts
  const initializeProfile = useCallback(async () => {
    if (isInitialized) {
      return;
    }

    setIsLoading(true);

    try {
      // V163.6.8: Use auth user from parent to eliminate duplicate auth calls
      console.log('🎯 V163.6.8: GlobalProfileContext using provided auth user');

      // Get user from auth context (passed from parent) to prevent duplicate auth requests
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const u = user;

      console.log('✅ V180.3.1: User authenticated:', u.id);
      setCurrentUser(u);

      // V180.3.1: Ensure user has legal consent record (auto-backfill for existing users)
      console.log('🚀 V180.3.1: Starting consent check for user:', u.id);
      try {
        await ensureUserConsent(u.id);
        console.log('✅ V180.3.1: Consent check complete');
      } catch (error) {
        console.error('❌ V180.3.1: Consent check FAILED:', error);
        console.error('❌ V180.3.1: Error details:', JSON.stringify(error, null, 2));
        // Continue anyway - don't block user
      }

      // 🚀 CACHE-FIRST: Check unified cache first
      const cached = getCachedProfile(u.id);
      if (cached) {
        setProfile(cached);
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // 🚀 FALLBACK: Fetch from database only if no valid cache
      // V151.19: Use maybeSingle() to handle new users gracefully
      // V161.11: Use profile coordinator instead of direct queries
      console.log('🚀 V161.11: GlobalProfileContext loading via coordinator');
      const profileData = await getUserCompleteProfile(u.id);

      if (!profileData) {
        console.log('🔍 V161.11: GlobalProfileContext - No profile data found (new user)');
        setIsLoading(false);
        return;
      }

      const full = { ...profileData, age: profileData.dob ? calcAge(profileData.dob) : undefined };

      setProfile(full);
      setCachedProfile(u.id, full);
      setIsInitialized(true);
    } catch (error) {
      console.error('GlobalProfileContext: Error during initialization:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]);

  // 🚨 NUCLEAR STABILIZATION: Auto-initialize when provider mounts with stable dependency
  useEffect(() => {
    initializeProfile();
  }, []); // Remove initializeProfile dependency to prevent re-initialization loops

  const updateProfile = useCallback(async (updates: any) => {
    if (!currentUser?.id) {
      return;
    }

    setIsLoading(true);

    try {
      console.log('🚀 GlobalProfileContext: updateProfile called with:', JSON.stringify(updates, null, 2));

      // Build users table update with correct column names
      const usersUpdate: any = {
        updated_at: new Date().toISOString()
      };

      if (updates.name !== undefined) usersUpdate.name = updates.name;
      if (updates.bio !== undefined) usersUpdate.bio = updates.bio;
      if (updates.gender !== undefined) usersUpdate.gender = updates.gender;

      // FIX: Use date_of_birth (DB column) instead of dob
      if (updates.dob !== undefined) {
        usersUpdate.date_of_birth = updates.dob;
      } else if (updates.date_of_birth !== undefined) {
        usersUpdate.date_of_birth = updates.date_of_birth;
      }

      console.log('📝 GlobalProfileContext: Updating users table with:', JSON.stringify(usersUpdate, null, 2));

      // Update users table and read back authoritative DB values
      const { data: updatedUser, error: userError } = await supabase
        .from('users')
        .update(usersUpdate)
        .eq('id', currentUser.id)
        .select('id, name, email, bio, date_of_birth, gender, photo_urls, trust_score, is_verified, updated_at')
        .single();

      if (userError) {
        console.error('❌ GlobalProfileContext: Users table update failed:', userError);
        throw userError;
      }

      console.log('✅ GlobalProfileContext: Users table updated:', JSON.stringify(updatedUser, null, 2));

      // Update profiles table if needed
      const profilesUpdate: any = {};
      if (updates.relationship_status !== undefined) profilesUpdate.relationship_status = updates.relationship_status;
      if (updates.dating_goal !== undefined) profilesUpdate.dating_goal = updates.dating_goal;

      if (Object.keys(profilesUpdate).length > 0) {
        profilesUpdate.updated_at = new Date().toISOString();

        console.log('📝 GlobalProfileContext: Updating profiles table with:', JSON.stringify(profilesUpdate, null, 2));

        const { error: profileError } = await supabase
          .from('profiles')
          .update(profilesUpdate)
          .eq('user_id', currentUser.id);

        if (profileError) {
          console.error('❌ GlobalProfileContext: Profiles table update failed:', profileError);
          throw profileError;
        }

        console.log('✅ GlobalProfileContext: Profiles table updated');
      }

      // 🚀 CACHE INVALIDATION: Clear cache BEFORE setting new cache
      invalidateProfileCache(currentUser.id);

      // Fetch complete profile with profiles join
      console.log('🔄 GlobalProfileContext: Fetching complete profile after update');
      const refreshed = await getUserCompleteProfile(currentUser.id);

      if (refreshed) {
        // Normalize date_of_birth to dob for backward compatibility
        const fullProfile = {
          ...refreshed,
          dob: refreshed.date_of_birth || refreshed.dob,
          age: (refreshed.date_of_birth || refreshed.dob) ? calcAge(refreshed.date_of_birth || refreshed.dob) : undefined
        };

        console.log('✅ GlobalProfileContext: Setting profile state with:', JSON.stringify(fullProfile, null, 2));

        // Update state immediately with authoritative DB values
        setProfile(fullProfile);
        setCachedProfile(currentUser.id, fullProfile);

        console.log('✅ GlobalProfileContext: Profile updated successfully');
      } else {
        console.warn('⚠️ GlobalProfileContext: Failed to fetch refreshed profile');
      }
    } catch (error) {
      console.error('❌ GlobalProfileContext: Error updating profile:', error);
      console.error('❌ GlobalProfileContext: Error details:', JSON.stringify(error, null, 2));
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.id]);

  const refreshProfile = useCallback(async () => {
    if (!currentUser?.id) return;

    invalidateProfileCache(currentUser.id);
    setIsInitialized(false);
    await initializeProfile();
  }, [currentUser?.id, initializeProfile]);

  return (
    <GlobalProfileContext.Provider value={{
      profile,
      currentUser,
      isLoading,
      isInitialized,
      updateProfile,
      refreshProfile,
      initializeProfile
    }}>
      {children}
    </GlobalProfileContext.Provider>
  );
}

export function useGlobalProfile() {
  const context = useContext(GlobalProfileContext);
  if (context === undefined) {
    throw new Error('useGlobalProfile must be used within a GlobalProfileProvider');
  }
  return context;
}
